// Shared legal body, rendered both on the standalone /settings/terms page
// (TermsAndConditionsPage.js) and inside the first-run onboarding wizard's
// acceptance step (OnboardingGate.js) — keeping one copy avoids the two
// drifting out of sync.
export default function LicenseAgreementText() {
  return (
    <div className="prose prose-sm max-w-none text-muted-foreground space-y-6 text-[13px] leading-relaxed">
      <p><strong>Effective Date:</strong> 10 August 2026</p>
      <p>
        This Software License Agreement (&ldquo;Agreement&rdquo;) is entered into as of the Effective Date above by
        and between the licensor of the Admitrix CRM platform (&ldquo;Licensor,&rdquo; &ldquo;Admitrix,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us&rdquo;) and YuHasPro (&ldquo;Licensee,&rdquo; &ldquo;Client,&rdquo; &ldquo;you&rdquo;). By accessing or
        using the Admitrix CRM software (the &ldquo;Software&rdquo;), Licensee agrees to be bound by the terms of
        this Agreement.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">1. License Grant</h2>
      <p>
        Subject to Licensee&rsquo;s continued compliance with this Agreement and timely payment of all applicable
        fees, Licensor grants Licensee a limited, non-exclusive, non-transferable, non-sublicensable license to
        access and use the Software solely for Licensee&rsquo;s internal business operations, and solely up to the
        usage limits and feature entitlements specified in Licensee&rsquo;s then-current subscription plan (the
        &ldquo;Plan&rdquo;), as shown in the License section of the Software. All rights not expressly granted to
        Licensee are reserved by Licensor.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">2. Ownership and Intellectual Property</h2>
      <p>
        The Software, including all source code, object code, architecture, design, workflows, and documentation,
        and all intellectual property rights therein, are and shall remain the sole and exclusive property of
        Licensor. This Agreement does not convey to Licensee any ownership right, title, or interest in the
        Software. Licensee retains ownership of the data it enters into the Software (&ldquo;Client Data&rdquo;),
        and grants Licensor a limited license to host, process, and use Client Data solely to provide, maintain,
        and support the Software.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">3. Permitted Use and Restrictions</h2>
      <p>
        The Software may be accessed only by Licensee&rsquo;s authorized staff for managing leads, enrollments,
        communications, and related administrative functions in the ordinary course of Licensee&rsquo;s business.
        Licensee shall not, and shall not permit any third party to: (a) copy, modify, or create derivative works
        of the Software; (b) reverse engineer, decompile, or disassemble the Software, except to the extent such
        restriction is prohibited by applicable law; (c) sell, resell, rent, lease, sublicense, or otherwise
        make the Software available to any third party; (d) use the Software to build a competing product or
        service; (e) exceed the usage limits (branches, users, leads, enrolled students, courses, or batches) of
        the then-current Plan; or (f) use the Software for any unlawful purpose or in violation of applicable data
        protection or consumer protection law.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">4. Fees and Plan Limits</h2>
      <p>
        Licensee&rsquo;s access to the Software, including the specific usage limits and optional feature
        add-ons enabled or excluded, is governed by the Plan agreed between the parties and is reflected in the
        License section of the Software. Licensor reserves the right to suspend or restrict access to any
        functionality that exceeds the entitlements of the current Plan. Upgrades, downgrades, or renewals shall
        be effected only through Licensor and are subject to separate written or invoiced agreement.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">5. Disclaimer of Warranties</h2>
      <p>
        THE SOFTWARE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTY OF ANY KIND,
        WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO ANY IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. LICENSOR DOES NOT WARRANT
        THAT THE SOFTWARE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE, OR THAT ANY DATA LOSS WILL NOT
        OCCUR. LICENSEE ACKNOWLEDGES THAT IT IS SOLELY RESPONSIBLE FOR MAINTAINING ITS OWN BACKUPS OF ANY CLIENT
        DATA IT CONSIDERS CRITICAL, EXCEPT TO THE EXTENT OTHERWISE AGREED IN WRITING.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">6. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL LICENSOR BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE,
        DATA, GOODWILL, OR BUSINESS OPPORTUNITY, ARISING OUT OF OR RELATED TO THIS AGREEMENT OR THE USE OF (OR
        INABILITY TO USE) THE SOFTWARE, REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF LICENSOR HAS BEEN
        ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. LICENSOR&rsquo;S AGGREGATE LIABILITY ARISING OUT OF OR RELATED
        TO THIS AGREEMENT SHALL NOT EXCEED THE TOTAL FEES ACTUALLY PAID BY LICENSEE TO LICENSOR FOR THE SOFTWARE
        IN THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">7. Indemnification by Licensee</h2>
      <p>
        Licensee shall defend, indemnify, and hold harmless Licensor and its officers, directors, employees,
        contractors, and agents from and against any and all claims, demands, losses, damages, liabilities,
        judgments, settlements, costs, and expenses (including reasonable legal fees) arising out of or relating
        to: (a) Licensee&rsquo;s or its staff&rsquo;s use or misuse of the Software; (b) Client Data, including any
        claim that Client Data, or Licensee&rsquo;s collection or use thereof, violates applicable law
        (including data protection, privacy, or consumer protection law) or infringes any third-party right;
        (c) any communications, messages, or content sent by Licensee or its staff to leads, students, or any
        third party through or in connection with the Software; (d) Licensee&rsquo;s breach of this Agreement or
        violation of applicable law; or (e) any dispute between Licensee and its own students, leads, staff, or
        customers. This indemnification obligation survives termination or expiry of this Agreement.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">8. Data Protection and Client Responsibility</h2>
      <p>
        Licensee is solely responsible for ensuring it has all necessary rights, consents, and lawful basis to
        collect, store, and process the personal data of leads, students, parents, and staff that it enters into
        the Software, and for its own compliance with applicable data protection and consumer protection laws.
        Licensor acts solely as a data processor/host on Licensee&rsquo;s behalf with respect to Client Data and
        bears no responsibility for the lawfulness, accuracy, or consequences of Licensee&rsquo;s data collection
        or use.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">9. Term and Termination</h2>
      <p>
        This Agreement remains in effect for the duration of the commercial arrangement between the parties, as
        reflected by the validity period of Licensee&rsquo;s Plan. Licensor may suspend or terminate access
        immediately, without liability, in the event of non-payment, breach of this Agreement, or use of the
        Software in a manner that Licensor reasonably believes exposes it to legal or security risk. Upon
        termination or expiry, all licenses granted hereunder immediately cease, and Licensor may, at its
        discretion, retain, export, or delete Client Data in accordance with its data retention practices then
        in effect.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">10. Support and Maintenance</h2>
      <p>
        Licensor will provide reasonable-effort support and maintenance for the Software consistent with the
        support tier of Licensee&rsquo;s Plan. Licensor does not guarantee any specific uptime, response time, or
        resolution time except as separately agreed in writing.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">11. Governing Law</h2>
      <p>
        This Agreement shall be governed by and construed in accordance with the laws of India, without regard
        to its conflict-of-laws principles, and the courts of competent jurisdiction in India shall have
        exclusive jurisdiction over any dispute arising out of or relating to this Agreement.
      </p>

      <h2 className="font-display text-lg font-bold text-foreground mt-8 mb-3">12. Contact</h2>
      <p>
        For questions about this Agreement or Licensee&rsquo;s current Plan, contact your Admitrix account
        representative.
      </p>
    </div>
  );
}
